import { useMemo, useState } from 'react';
import { ActivityLogRow } from './ActivityLogRow';
import { ActivitySummaryStrip } from './ActivitySummaryStrip';
import { TopDestinationsList } from './TopDestinationsList';
import { TabFilter, type ActivityFilter } from './TabFilter';
import type { ActivityRow, Destination, Summary } from './derive';

export function ActivityLogCard({
  rows,
  summary,
  destinations,
  rangeLabel,
  rangeControl,
  loading,
  error,
}: {
  rows: ActivityRow[];
  summary: Summary;
  destinations: Destination[];
  rangeLabel?: string;
  rangeControl?: React.ReactNode;
  loading?: boolean;
  error?: string | null;
}) {
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const filtered = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.kind === filter)),
    [filter, rows],
  );

  return (
    <div className="flex flex-col gap-3.5 rounded-2xl border border-border bg-surface p-[22px] shadow-shell">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[14px] font-extrabold tracking-[0.06em]">ACTIVITY LOG</div>
          <div className="mt-0.5 text-[11px] text-muted">
            Dispatches, tasks, and takeovers{rangeLabel ? ` — ${rangeLabel}` : ''}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {rangeControl}
          <TabFilter value={filter} onChange={setFilter} />
        </div>
      </div>

      <ActivitySummaryStrip summary={summary} />
      <TopDestinationsList destinations={destinations} rangeLabel={rangeLabel} />

      {error && (
        <div className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">{error}</div>
      )}

      <div className="flex flex-col">
        {filtered.map((r) => (
          <ActivityLogRow key={r.id} row={r} />
        ))}
        {!loading && filtered.length === 0 && !error && (
          <div className="px-1 py-8 text-center text-sm text-muted">
            No events in this range.
          </div>
        )}
        {loading && filtered.length === 0 && (
          <div className="px-1 py-8 text-center text-sm text-muted">Loading…</div>
        )}
      </div>
    </div>
  );
}
